import React from 'react';
import LegalLayout, { TocSection } from '../../components/legal/LegalLayout';
import { useLanguage } from '../../context/LanguageContext';

const PrivacyPolicy: React.FC = () => {
  const { language } = useLanguage();

  if (language === 'ar') {
    const arSections: TocSection[] = [
      { id: 'veri-sorumlusu', title: '1. هوية مسؤول البيانات' },
      { id: 'toplanan-veriler', title: '2. البيانات الشخصية التي يتم جمعها' },
      { id: 'isleme-amaclari', title: '3. أغراض معالجة البيانات وأسسها' },
      { id: 'verilerin-aktarimi', title: '4. نقل البيانات إلى أطراف ثالثة' },
      { id: 'kvkk-haklari', title: '5. حقوقكم بموجب KVKK و GDPR' },
      { id: 'veri-guvenligi', title: '6. أمن البيانات وفترات الاحتفاظ بها' },
      { id: 'iletisim-basvuru', title: '7. قنوات التواصل وتقديم الطلبات' },
    ];

    return (
      <LegalLayout
        title="سياسة الخصوصية وإشعار معالجة البيانات الشخصية"
        subtitle="حماية البيانات الشخصية والخصوصية"
        lastUpdated="01 سبتمبر 2026"
        activeTab="privacy"
        sections={arSections}
      >
        {/* Intro Note */}
        <div className="bg-brand-purple-50/70 border border-brand-purple-100 p-6 rounded-2xl text-brand-purple-900 space-y-3">
          <div className="flex items-center space-x-3 font-bold text-base">
            <span className="w-8 h-8 rounded-full bg-brand-purple-900 text-brand-yellow-400 flex items-center justify-center font-bold text-sm">
              i
            </span>
            <span>إشعار هام</span>
          </div>
          <p className="text-sm text-gray-700 leading-relaxed font-light">
            تم إعداد هذا الإشعار بموجب المادة 10 من قانون حماية البيانات الشخصية رقم 6698 (KVKK) واللائحة العامة لحماية البيانات في الاتحاد الأوروبي (GDPR)، لإعلام زوار موقعنا ومستخدمي قنوات التواصل الخاصة بنا بكيفية معالجة بياناتهم وحمايتها.
          </p>
        </div>

        {/* Section 1 */}
        <section id="veri-sorumlusu" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg ml-3">01.</span>
            هوية مسؤول البيانات
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            بموجب المادة 3 من قانون KVKK، تتم معالجة بياناتكم الشخصية بصفتنا مسؤول البيانات من قبل <strong className="text-brand-purple-900 font-semibold">حركة المرأة الحرة (TJA) / شبكة التضامن النسائي</strong> في النطاق الموضح أدناه.
          </p>
          <div className="bg-gray-50 p-6 rounded-2xl space-y-2 text-sm text-gray-700 border border-gray-100">
            <p><strong>اسم المؤسسة:</strong> Tevgera Jinên Azad (TJA)</p>
            <p><strong>البريد الإلكتروني:</strong> tja.bilgi@gmail.com</p>
            <p><strong>الموقع الإلكتروني:</strong> https://tevgerajinenazad.com</p>
          </div>
        </section>

        {/* Section 2 */}
        <section id="toplanan-veriler" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg ml-3">02.</span>
            البيانات الشخصية التي يتم جمعها وطرق جمعها
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            عند زيارتكم لموقعنا أو التواصل معنا عبر النماذج، قد نقوم بجمع بياناتكم في الفئات التالية:
          </p>
          <ul className="space-y-3 text-gray-600 font-light list-disc list-inside bg-gray-50 p-6 rounded-2xl border border-gray-100">
            <li><strong className="text-brand-purple-900 font-medium">بيانات نموذج الاتصال:</strong> الاسم، اللقب، عنوان البريد الإلكتروني والملاحظات أو الرسائل التي ترسلونها.</li>
            <li><strong className="text-brand-purple-900 font-medium">سجلات الوصول والخادم (Logs):</strong> عنوان الـ IP الخاص بكم، وتاريخ ووقت الوصول، ونوع المتصفح ونظام التشغيل، والرابط المرجعي (وفقاً للمتطلبات الأمنية لحماية الخوادم).</li>
            <li><strong className="text-brand-purple-900 font-medium">بيانات ملفات تعريف الارتباط:</strong> ملفات تعريف الارتباط التقنية المستخدمة لتذكر تفضيلاتكم اللغوية وضمان تجربة التصفح الأساسية.</li>
          </ul>
        </section>

        {/* Section 3 */}
        <section id="isleme-amaclari" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg ml-3">03.</span>
            أغراض معالجة البيانات وأسسها القانونية
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            تتم معالجة بياناتكم الشخصية التي تم جمعها استناداً إلى الأسس القانونية المحددة في المادتين 5 و 6 من قانون KVKK والمادة 6 من اللائحة العامة لحماية البيانات (GDPR) للأغراض التالية:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
            <div className="p-5 bg-white rounded-2xl border border-brand-purple-100 shadow-sm space-y-2">
              <h4 className="font-bold text-brand-purple-900 text-sm">طلبات الاتصال والتضامن</h4>
              <p className="text-xs text-gray-500 font-light leading-relaxed">
                تقييم والرد على الاستفسارات والآراء والمقترحات وطلبات الدعم المقدمة من خلال نماذج الاتصال.
              </p>
            </div>
            <div className="p-5 bg-white rounded-2xl border border-brand-purple-100 shadow-sm space-y-2">
              <h4 className="font-bold text-brand-purple-900 text-sm">أمن المعلومات وإدارة النظام</h4>
              <p className="text-xs text-gray-500 font-light leading-relaxed">
                ضمان أمان موقعنا الإلكتروني، ومكافحة الهجمات السيبرانية والامتثال للالتزامات القانونية بحفظ السجلات.
              </p>
            </div>
            <div className="p-5 bg-white rounded-2xl border border-brand-purple-100 shadow-sm space-y-2">
              <h4 className="font-bold text-brand-purple-900 text-sm">نشر التقارير والمحتوى</h4>
              <p className="text-xs text-gray-500 font-light leading-relaxed">
                توفير وصول سلس للتقارير والأبحاث المنشورة والأخبار والبيانات الصحفية.
              </p>
            </div>
            <div className="p-5 bg-white rounded-2xl border border-brand-purple-100 shadow-sm space-y-2">
              <h4 className="font-bold text-brand-purple-900 text-sm">الامتثال للالتزامات القانونية</h4>
              <p className="text-xs text-gray-500 font-light leading-relaxed">
                تلبية الالتزامات القانونية بالإفصاح وتقديم المعلومات في حال طلبتها الجهات القضائية أو الرسمية المختصة قانوناً.
              </p>
            </div>
          </div>
        </section>

        {/* Section 4 */}
        <section id="verilerin-aktarimi" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg ml-3">04.</span>
            نقل البيانات الشخصية إلى أطراف ثالثة
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            تؤكد TJA أنها <strong className="text-brand-purple-900 font-semibold">لا تبيع بياناتكم الشخصية ولا تؤجرها ولا تشاركها نهائياً مع شركات الإعلانات أو لأغراض تجارية</strong>.
          </p>
          <p className="text-gray-600 leading-relaxed font-light">
            لا يمكن مشاركة بياناتكم إلا بموافقتكم الصريحة أو عند وجود إلزام قانوني صريح (مثل قرار قضائي أو طلب رسمي ملزم من سلطة إدارية مختصة) وحصراً مع الجهات الرسمية ذات الصلة.
          </p>
        </section>

        {/* Section 5 */}
        <section id="kvkk-haklari" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg ml-3">05.</span>
            حقوقكم بموجب المادة 11 من KVKK و GDPR
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            بصفتكم صاحب البيانات، يحق لكم بموجب المادة 11 من قانون KVKK والمواد 15-22 من اللائحة العامة لحماية البيانات (GDPR) التمتع بالحقوق التالية:
          </p>
          <div className="space-y-3">
            {[
              'معرفة ما إذا كانت بياناتكم الشخصية قيد المعالجة أم لا،',
              'طلب معلومات مفصلة في حال تمت معالجة بياناتكم الشخصية،',
              'معرفة الغرض من معالجة البيانات والتأكد من استخدامها وفقاً لهذا الغرض،',
              'معرفة الأطراف الثالثة التي تم نقل البيانات إليها محلياً أو خارج البلاد،',
              'طلب تصحيح وتعديل البيانات في حال كانت غير كاملة أو غير دقيقة،',
              'طلب حذف أو إتلاف البيانات بموجب المادة 7 من قانون KVKK،',
              'الاعتراض على أي نتائج قد تكون مجحفة أو سلبية بحقكم ناجمة عن التحليل الحصري للبيانات عبر الأنظمة الآلية،',
              'طلب التعويض عن الأضرار في حال لحق بكم أي ضرر نتيجة معالجة غير قانونية لبياناتكم.',
            ].map((hak, i) => (
              <div key={i} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                <span className="w-5 h-5 rounded-full bg-brand-purple-100 text-brand-purple-700 flex items-center justify-center font-bold text-xs shrink-0 mt-0.5 ml-3">
                  ✓
                </span>
                <span className="text-sm text-gray-700 font-light">{hak}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Section 6 */}
        <section id="veri-guvenligi" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg ml-3">06.</span>
            أمن البيانات وفترات الاحتفاظ بها
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            نطبق بروتوكولات تشفير آمنة (SSL/TLS) وتدابير أمنية تقنية وتنظيمية متقدمة لمنع وصول الأشخاص غير المصرح لهم إلى بياناتكم أو فقدانها أو إساءة استخدامها.
          </p>
          <p className="text-gray-600 leading-relaxed font-light">
            يتم حذف بيانات الاتصال الخاصة بكم أو جعلها مجهولة الهوية بشكل آمن وموثوق خلال فترة الإتلاف الدورية الأولى فور الرد على طلبكم واستيفاء فترات الحفظ القانونية.
          </p>
        </section>

        {/* Section 7 */}
        <section id="iletisim-basvuru" className="scroll-mt-32 space-y-4">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg ml-3">07.</span>
            قنوات التواصل وتقديم الطلبات
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            لممارسة حقوقكم القانونية، يمكنكم تقديم طلبكم الرسمي مصحوباً بالوثائق التي تثبت هويتكم عبر القناة المعتمدة أدناه:
          </p>
          <div className="bg-brand-purple-900 text-white p-8 rounded-3xl space-y-4 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-brand-yellow-400/10 rounded-full blur-xl"></div>
            <h4 className="font-serif font-bold text-xl text-brand-yellow-400">مكتب اتصال مسؤول البيانات</h4>
            <p className="text-sm text-brand-purple-100 font-light leading-relaxed">
              سيتم فحص طلباتكم والبت فيها والرد عليها مجاناً في أقرب وقت ممكن وبحد أقصى خلال <strong>30 (ثلاثين) يوماً</strong> وفقاً للمادة 13 من قانون KVKK.
            </p>
            <div className="pt-2 flex items-center space-x-3">
              <span className="text-xs uppercase tracking-widest text-brand-purple-200 ml-2">البريد الإلكتروني:</span>
              <a href="mailto:tja.bilgi@gmail.com" className="font-bold text-white hover:text-brand-yellow-400 underline">
                tja.bilgi@gmail.com
              </a>
            </div>
          </div>
        </section>
      </LegalLayout>
    );
  }

  if (language === 'en') {
    const enSections: TocSection[] = [
      { id: 'veri-sorumlusu', title: '1. Identity of the Data Controller' },
      { id: 'toplanan-veriler', title: '2. Personal Data Collected and Methods' },
      { id: 'isleme-amaclari', title: '3. Purposes and Legal Grounds of Processing' },
      { id: 'verilerin-aktarimi', title: '4. Transfer of Personal Data to Third Parties' },
      { id: 'kvkk-haklari', title: '5. Your Rights Under KVKK & GDPR' },
      { id: 'veri-guvenligi', title: '6. Data Security and Retention Periods' },
      { id: 'iletisim-basvuru', title: '7. Application and Contact Channels' },
    ];

    return (
      <LegalLayout
        title="Privacy Policy and Clarification Text"
        subtitle="Protection of Personal Data and Privacy"
        lastUpdated="September 01, 2026"
        activeTab="privacy"
        sections={enSections}
      >
        {/* Intro Note */}
        <div className="bg-brand-purple-50/70 border border-brand-purple-100 p-6 rounded-2xl text-brand-purple-900 space-y-3">
          <div className="flex items-center space-x-3 font-bold text-base">
            <span className="w-8 h-8 rounded-full bg-brand-purple-900 text-brand-yellow-400 flex items-center justify-center font-bold text-sm">
              i
            </span>
            <span>Important Information</span>
          </div>
          <p className="text-sm text-gray-700 leading-relaxed font-light">
            This Clarification Text has been prepared in accordance with Article 10 of the Law on the Protection of Personal Data No. 6698 ("KVKK") and the European Union General Data Protection Regulation ("GDPR") to inform visitors of our website and users of our communication channels regarding the processing and safeguarding of their personal data.
          </p>
        </div>

        {/* Section 1 */}
        <section id="veri-sorumlusu" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg mr-3">01.</span>
            Identity of the Data Controller
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            Pursuant to Article 3 of the KVKK, your personal data is processed by <strong className="text-brand-purple-900 font-semibold">Tevgera Jinên Azad (TJA) / Women's Solidarity Network</strong> as the data controller within the scope detailed below.
          </p>
          <div className="bg-gray-50 p-6 rounded-2xl space-y-2 text-sm text-gray-700 border border-gray-100">
            <p><strong>Organization Name:</strong> Tevgera Jinên Azad (TJA)</p>
            <p><strong>Email Address:</strong> tja.bilgi@gmail.com</p>
            <p><strong>Website:</strong> https://tevgerajinenazad.com</p>
          </div>
        </section>

        {/* Section 2 */}
        <section id="toplanan-veriler" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg mr-3">02.</span>
            Personal Data Collected and Collection Methods
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            When you visit our website or communicate with us, the following categories of personal data may be collected:
          </p>
          <ul className="space-y-3 text-gray-600 font-light list-disc list-inside bg-gray-50 p-6 rounded-2xl border border-gray-100">
            <li><strong className="text-brand-purple-900 font-medium">Contact Form Data:</strong> Your name, surname, email address, and any personal messages or notes you provide in the form.</li>
            <li><strong className="text-brand-purple-900 font-medium">Access & Server Log Records:</strong> Your IP address, access date and time, browser type, operating system, and referring URL (collected strictly for server security and legal compliance).</li>
            <li><strong className="text-brand-purple-900 font-medium">Cookie Data:</strong> Technical cookies used to store your language preferences and provide core navigation functionality.</li>
          </ul>
        </section>

        {/* Section 3 */}
        <section id="isleme-amaclari" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg mr-3">03.</span>
            Purposes and Legal Grounds for Processing
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            Your collected personal data is processed based on the legal grounds set forth in Articles 5 and 6 of the KVKK and Article 6 of the GDPR for the following purposes:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
            <div className="p-5 bg-white rounded-2xl border border-brand-purple-100 shadow-sm space-y-2">
              <h4 className="font-bold text-brand-purple-900 text-sm">Communication and Support Inquiries</h4>
              <p className="text-xs text-gray-500 font-light leading-relaxed">
                Evaluating and responding to questions, feedback, proposals, and support requests submitted via our contact forms.
              </p>
            </div>
            <div className="p-5 bg-white rounded-2xl border border-brand-purple-100 shadow-sm space-y-2">
              <h4 className="font-bold text-brand-purple-900 text-sm">Information Security and System Operations</h4>
              <p className="text-xs text-gray-500 font-light leading-relaxed">
                Ensuring website security, preventing malicious cyber activities, and fulfilling statutory server log retention requirements.
              </p>
            </div>
            <div className="p-5 bg-white rounded-2xl border border-brand-purple-100 shadow-sm space-y-2">
              <h4 className="font-bold text-brand-purple-900 text-sm">Publishing and Research Dissemination</h4>
              <p className="text-xs text-gray-500 font-light leading-relaxed">
                Providing seamless access to published reports, press releases, articles, and research materials.
              </p>
            </div>
            <div className="p-5 bg-white rounded-2xl border border-brand-purple-100 shadow-sm space-y-2">
              <h4 className="font-bold text-brand-purple-900 text-sm">Fulfillment of Legal Obligations</h4>
              <p className="text-xs text-gray-500 font-light leading-relaxed">
                Complying with statutory reporting obligations if mandated by competent public and administrative authorities.
              </p>
            </div>
          </div>
        </section>

        {/* Section 4 */}
        <section id="verilerin-aktarimi" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg mr-3">04.</span>
            Transfer of Personal Data to Third Parties
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            TJA <strong>never sells, rents, or shares your personal data with third parties or advertising firms for commercial purposes</strong>.
          </p>
          <p className="text-gray-600 leading-relaxed font-light">
            Your data may only be shared with authorized public bodies when required by explicit statutory mandate (such as court orders or competent judicial/administrative requests) or with your prior explicit consent.
          </p>
        </section>

        {/* Section 5 */}
        <section id="kvkk-haklari" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg mr-3">05.</span>
            Your Rights Under KVKK Article 11 & GDPR Articles 15-22
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            As a data subject, you have the following rights under KVKK Article 11 and GDPR Articles 15–22:
          </p>
          <div className="space-y-3">
            {[
              'To learn whether your personal data is being processed,',
              'To request information if your personal data has been processed,',
              'To learn the purpose of processing and whether it is being used in accordance with its purpose,',
              'To know the third parties to whom data is transferred domestically or abroad,',
              'To request correction of incomplete or inaccurate data,',
              'To request the deletion or destruction of personal data under statutory conditions,',
              'To object to adverse outcomes arising solely through automated decision-making systems,',
              'To claim compensation for damages suffered due to unlawful processing.',
            ].map((right, i) => (
              <div key={i} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                <span className="w-5 h-5 rounded-full bg-brand-purple-100 text-brand-purple-700 flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">
                  ✓
                </span>
                <span className="text-sm text-gray-700 font-light">{right}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Section 6 */}
        <section id="veri-guvenligi" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg mr-3">06.</span>
            Data Security and Retention Periods
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            State-of-the-art administrative and technical security measures, including SSL/TLS encryption, are implemented to prevent unauthorized access, loss, or corruption of your personal data.
          </p>
          <p className="text-gray-600 leading-relaxed font-light">
            Contact data is securely deleted or anonymized during the first routine destruction cycle once your inquiry has been processed and statutory retention periods have lapsed.
          </p>
        </section>

        {/* Section 7 */}
        <section id="iletisim-basvuru" className="scroll-mt-32 space-y-4">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg mr-3">07.</span>
            Application and Contact Channels
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            To exercise your rights under KVKK and GDPR, you may submit your inquiry along with verifying identification through the following channel:
          </p>
          <div className="bg-brand-purple-900 text-white p-8 rounded-3xl space-y-4 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-brand-yellow-400/10 rounded-full blur-xl"></div>
            <h4 className="font-serif font-bold text-xl text-brand-yellow-400">Data Controller Contact Office</h4>
            <p className="text-sm text-brand-purple-100 font-light leading-relaxed">
              Your inquiries will be resolved free of charge as soon as possible and within <strong>30 (thirty) days</strong> at the latest pursuant to Article 13 of the KVKK.
            </p>
            <div className="pt-2 flex items-center space-x-3">
              <span className="text-xs uppercase tracking-widest text-brand-purple-200">Email:</span>
              <a href="mailto:tja.bilgi@gmail.com" className="font-bold text-white hover:text-brand-yellow-400 underline">
                tja.bilgi@gmail.com
              </a>
            </div>
          </div>
        </section>
      </LegalLayout>
    );
  }

  if (language === 'krm') {
    const krmSections: TocSection[] = [
      { id: 'veri-sorumlusu', title: '1. Nasnameya Berpirsê Daneyan' },
      { id: 'toplanan-veriler', title: '2. Daneyên Kesane yên Tên Komkirin' },
      { id: 'isleme-amaclari', title: '3. Armanc û Bingeha Hiqûqî ya Daneyan' },
      { id: 'verilerin-aktarimi', title: '4. Veguhestina Daneyan bo Kesên Sêyem' },
      { id: 'kvkk-haklari', title: '5. Mafên We yên li Gorî KVKK û GDPR' },
      { id: 'veri-guvenligi', title: '6. Ewlehiya Daneyan û Demên Parastinê' },
      { id: 'iletisim-basvuru', title: '7. Qadên Serlêdan û Têkiliyê' },
    ];

    return (
      <LegalLayout
        title="Siyaseta Nepeniyê û Daxuyaniya Ronîkirinê"
        subtitle="Parastina Daneyên Kesane û Nepenî"
        lastUpdated="01 Êlûn 2026"
        activeTab="privacy"
        sections={krmSections}
      >
        {/* Intro Note */}
        <div className="bg-brand-purple-50/70 border border-brand-purple-100 p-6 rounded-2xl text-brand-purple-900 space-y-3">
          <div className="flex items-center space-x-3 font-bold text-base">
            <span className="w-8 h-8 rounded-full bg-brand-purple-900 text-brand-yellow-400 flex items-center justify-center font-bold text-sm">
              i
            </span>
            <span>Agahdariya Girîng</span>
          </div>
          <p className="text-sm text-gray-700 leading-relaxed font-light">
            Ev Daxuyaniya Ronîkirinê, li gorî Madeya 10an a Qanûna Parastina Daneyên Kesane ya Jimar 6698 ("KVKK") û Rêziknameya Giştî ya Parastina Daneyan a Yekîtiya Ewropayê ("GDPR"), ji bo agahdarkirina mêvanên malpera me û bikarhênerên qadên ragihandinê yên li ser pêvajoya daneyan hatiye amadekirin.
          </p>
        </div>

        {/* Section 1 */}
        <section id="veri-sorumlusu" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg mr-3">01.</span>
            Nasnameya Berpirsê Daneyan
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            Li gorî Madeya 3yan a KVKK'ê, daneyên we yên kesane wekî berpirsê daneyan ji aliyê <strong className="text-brand-purple-900 font-semibold">Tevgera Jinên Azad (TJA) / Tora Piştevaniya Jinan</strong> ve di çarçoveya li jêr de tên hilanîn û pêvajokirin.
          </p>
          <div className="bg-gray-50 p-6 rounded-2xl space-y-2 text-sm text-gray-700 border border-gray-100">
            <p><strong>Navê Saziyê:</strong> Tevgera Jinên Azad (TJA)</p>
            <p><strong>Navnîşana E-Peyamê:</strong> tja.bilgi@gmail.com</p>
            <p><strong>Malper:</strong> https://tevgerajinenazad.com</p>
          </div>
        </section>

        {/* Section 2 */}
        <section id="toplanan-veriler" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg mr-3">02.</span>
            Daneyên Kesane yên Tên Komkirin û Rêbazên Bidestxistinê
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            Dema hûn serdana malpera me dikin an jî bi rêya formên me bi me re dikevin têkiliyê, daneyên we yên di van kategoriyan de dikarin werin komkirin:
          </p>
          <ul className="space-y-3 text-gray-600 font-light list-disc list-inside bg-gray-50 p-6 rounded-2xl border border-gray-100">
            <li><strong className="text-brand-purple-900 font-medium">Daneyên Forma Têkiliyê:</strong> Nav, paşnav, navnîşana e-peyamê û nîşeyên kesane yên ku hûn di qada peyamê de dişînin.</li>
            <li><strong className="text-brand-purple-900 font-medium">Tomarên Gihîştin û Serverê (Logs):</strong> Navnîşana IP'ya we, dîrok û saeta gihîştinê, cûreya gerokê, pergala xebitandinê û navnîşana URL'ya referansê (ji bo pêdiviyên ewlehiya serverê).</li>
            <li><strong className="text-brand-purple-900 font-medium">Daneyên Çerezan:</strong> Çerezên teknîkî yên ku ji bo bîranîna tercîhên ziman û pêşkêşkirina ezmûna gerokê tên bikaranîn.</li>
          </ul>
        </section>

        {/* Section 3 */}
        <section id="isleme-amaclari" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg mr-3">03.</span>
            Armanc û Bingeha Hiqûqî ya Pêvajoya Daneyan
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            Daneyên we yên kesane li ser esasê bingehên hiqûqî yên Madeya 5 û 6an a KVKK'ê û Madeya 6an a GDPR'ê ji bo van armancan tên xebitandin:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
            <div className="p-5 bg-white rounded-2xl border border-brand-purple-100 shadow-sm space-y-2">
              <h4 className="font-bold text-brand-purple-900 text-sm">Daxwazên Têkilî û Piştgiriyê</h4>
              <p className="text-xs text-gray-500 font-light leading-relaxed">
                Nirxandin û bersivdayîna pirs, nêrîn, pêşniyar û daxwazên piştgiriyê yên bi rêya formên têkiliyê hatine şandin.
              </p>
            </div>
            <div className="p-5 bg-white rounded-2xl border border-brand-purple-100 shadow-sm space-y-2">
              <h4 className="font-bold text-brand-purple-900 text-sm">Ewlehiya Agahdariyê û Rêvebiriya Pergalê</h4>
              <p className="text-xs text-gray-500 font-light leading-relaxed">
                Dabînkirina ewlehiya malperê, astengkirina êrîşên sîber û bicîhanîna erkên qanûnî yên tomarkirina logan.
              </p>
            </div>
            <div className="p-5 bg-white rounded-2xl border border-brand-purple-100 shadow-sm space-y-2">
              <h4 className="font-bold text-brand-purple-900 text-sm">Pêşkêşkirina Weşan û Raporan</h4>
              <p className="text-xs text-gray-500 font-light leading-relaxed">
                Gihîştina hêsan a bi rapor, nûçe û daxuyaniyên çapemeniyê yên weşandî re.
              </p>
            </div>
            <div className="p-5 bg-white rounded-2xl border border-brand-purple-100 shadow-sm space-y-2">
              <h4 className="font-bold text-brand-purple-900 text-sm">Bicîhanîna Erkên Qanûnî</h4>
              <p className="text-xs text-gray-500 font-light leading-relaxed">
                Di rewşa daxwaza saziyên fermî û rayedar de, bicîhanîna erkên agahdarkirinê yên qanûnî.
              </p>
            </div>
          </div>
        </section>

        {/* Section 4 */}
        <section id="verilerin-aktarimi" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg mr-3">04.</span>
            Veguhestina Daneyên Kesane bo Kesên Sêyem
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            TJA, daneyên we yên kesane <strong>ji bo mebestên bazirganî nafiroşe, bi kirê nade û bi şîrketên reklamê re parve nake</strong>.
          </p>
          <p className="text-gray-600 leading-relaxed font-light">
            Daneyên we tenê bi erêkirina we ya eşkere an jî di rewşên mecbûriyeta qanûnî de (biryara dadgehê, daxwaza saziyên fermî yên rayedar) bi saziyên fermî yên peywendîdar re dikarin werin parvekirin.
          </p>
        </section>

        {/* Section 5 */}
        <section id="kvkk-haklari" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg mr-3">05.</span>
            Mafên We yên li Gorî KVKK Madeya 11 û GDPR Madeya 15-22
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            Wek xwediyê daneyan li gorî qanûnên parastina daneyan ev mafên we hene:
          </p>
          <div className="space-y-3">
            {[
              'Hûn bizanibin ka daneyên we yên kesane tên pêvajokirin an na,',
              'Heke hatibin pêvajokirin, daxwaza agahdariya derbarê wê de bikin,',
              'Armanca pêvajoyê û li gorî armancê bikaranîna wan hîn bibin,',
              'Kesên sêyem ên ku daneyên we bo wan hatine veguhestin bizanibin,',
              'Daxwaza sererastkirina daneyên kêm an şaş bikin,',
              'Di çarçoveya qanûnê de daxwaza jêbirin an tune kirina daneyan bikin,',
              'Li dijî encamên neyînî yên ku bi tenê bi pergalên otomatîk derketine holê derkevin,',
              'Di rewşa zirardîtina ji ber pêvajoyên li dijî qanûnê de daxwaza tazmînatê bikin.',
            ].map((maf, i) => (
              <div key={i} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                <span className="w-5 h-5 rounded-full bg-brand-purple-100 text-brand-purple-700 flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">
                  ✓
                </span>
                <span className="text-sm text-gray-700 font-light">{maf}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Section 6 */}
        <section id="veri-guvenligi" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg mr-3">06.</span>
            Ewlehiya Daneyan û Demên Parastinê
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            Ji bo pêşîlêgirtina gihîştina kesên nedestûrdar, windabûn an xisara daneyan, protokolên şîfrekirinê yên SSL/TLS û tedbîrên ewlehiya teknîkî ya nûjen tên sepandin.
          </p>
          <p className="text-gray-600 leading-relaxed font-light">
            Daneyên forma têkiliyê, piştî bersivdana daxwazê û bi dawîbûna demên qanûnî yên ragirtinê, bi ewlehî tên jêbirin an anonîm kirin.
          </p>
        </section>

        {/* Section 7 */}
        <section id="iletisim-basvuru" className="scroll-mt-32 space-y-4">
          <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
            <span className="text-brand-purple-500 font-mono text-lg mr-3">07.</span>
            Qadên Serlêdan û Têkiliyê
          </h2>
          <p className="text-gray-600 leading-relaxed font-light">
            Ji bo bikaranîna mafên xwe yên parastina daneyan, hûn dikarin bi rêya navnîşana jêrîn serî li me bidin:
          </p>
          <div className="bg-brand-purple-900 text-white p-8 rounded-3xl space-y-4 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-brand-yellow-400/10 rounded-full blur-xl"></div>
            <h4 className="font-serif font-bold text-xl text-brand-yellow-400">Ofîsa Têkiliyê ya Berpirsê Daneyan</h4>
            <p className="text-sm text-brand-purple-100 font-light leading-relaxed">
              Serlêdanên we dê herî dereng di nava <strong>30 (sî) rojan</strong> de bê pere werin encamdan.
            </p>
            <div className="pt-2 flex items-center space-x-3">
              <span className="text-xs uppercase tracking-widest text-brand-purple-200">E-Peyam:</span>
              <a href="mailto:tja.bilgi@gmail.com" className="font-bold text-white hover:text-brand-yellow-400 underline">
                tja.bilgi@gmail.com
              </a>
            </div>
          </div>
        </section>
      </LegalLayout>
    );
  }

  const sections: TocSection[] = [
    { id: 'veri-sorumlusu', title: '1. Veri Sorumlusunun Kimliği' },
    { id: 'toplanan-veriler', title: '2. Toplanan Kişisel Veriler' },
    { id: 'isleme-amaclari', title: '3. Verilerin İşlenme Amaçları' },
    { id: 'verilerin-aktarimi', title: '4. Verilerin Üçüncü Şahıslara Aktarımı' },
    { id: 'kvkk-haklari', title: '5. KVKK Madde 11 Kapsamındaki Haklarınız' },
    { id: 'veri-guvenligi', title: '6. Veri Güvenliği ve Saklama Süreleri' },
    { id: 'iletisim-basvuru', title: '7. Başvuru ve İletişim Kanalları' },
  ];

  return (
    <LegalLayout
      title="Gizlilik Politikası ve KVKK Aydınlatma Metni"
      subtitle="Kişisel Verilerin Korunması ve Gizlilik"
      lastUpdated="01 Eylül 2026"
      activeTab="privacy"
      sections={sections}
    >
      {/* Intro Note */}
      <div className="bg-brand-purple-50/70 border border-brand-purple-100 p-6 rounded-2xl text-brand-purple-900 space-y-3">
        <div className="flex items-center space-x-3 font-bold text-base">
          <span className="w-8 h-8 rounded-full bg-brand-purple-900 text-brand-yellow-400 flex items-center justify-center font-bold text-sm">
            i
          </span>
          <span>Önemli Bilgilendirme</span>
        </div>
        <p className="text-sm text-gray-700 leading-relaxed font-light">
          İşbu Aydınlatma Metni, 6698 sayılı Kişisel Verilerin Korunması Kanunu (“KVKK”) m. 10 ve Avrupa Birliği Genel Veri Koruma Tüzüğü (“GDPR”) uyarınca, web sitemizi ziyaret eden ve iletişim kanallarımızı kullanan kişileri bilgilendirmek amacıyla hazırlanmıştır.
        </p>
      </div>

      {/* Section 1 */}
      <section id="veri-sorumlusu" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
        <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
          <span className="text-brand-purple-500 font-mono text-lg mr-3">01.</span>
          Veri Sorumlusunun Kimliği
        </h2>
        <p className="text-gray-600 leading-relaxed font-light">
          KVKK m. 3 uyarınca, kişisel verileriniz veri sorumlusu sıfatıyla <strong className="text-brand-purple-900 font-semibold">Tevgera Jinên Azad (TJA) / Kadın Dayanışma Ağı</strong> tarafından aşağıda açıklanan kapsamda işlenmektedir.
        </p>
        <div className="bg-gray-50 p-6 rounded-2xl space-y-2 text-sm text-gray-700 border border-gray-100">
          <p><strong>Kurum Ünvanı:</strong> Tevgera Jinên Azad (TJA)</p>
          <p><strong>E-Posta Adresi:</strong> tja.bilgi@gmail.com</p>
          <p><strong>Web Sitesi:</strong> https://tevgerajinenazad.com</p>
        </div>
      </section>

      {/* Section 2 */}
      <section id="toplanan-veriler" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
        <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
          <span className="text-brand-purple-500 font-mono text-lg mr-3">02.</span>
          Toplanan Kişisel Veriler ve Elde Etme Yöntemleri
        </h2>
        <p className="text-gray-600 leading-relaxed font-light">
          Sitemizi ziyaret ettiğinizde veya bizimle iletişime geçtiğinizde aşağıdaki kategorilerde yer alan verileriniz toplanabilmektedir:
        </p>
        <ul className="space-y-3 text-gray-600 font-light list-disc list-inside bg-gray-50 p-6 rounded-2xl border border-gray-100">
          <li><strong className="text-brand-purple-900 font-medium">İletişim Formu Verileri:</strong> Adınız, soyadınız, e-posta adresiniz ve mesaj alanında ilettiğiniz kişisel notlar.</li>
          <li><strong className="text-brand-purple-900 font-medium">Erişim ve Log Kayıtları:</strong> IP adresiniz, erişim tarih ve saatiniz, tarayıcı türünüz, işletim sisteminiz ve yönlendiren URL adresi (Sunucu güvenliği zorunluluğu gereği).</li>
          <li><strong className="text-brand-purple-900 font-medium">Çerez Verileri:</strong> Sitedeki dil tercihleriniz ve temel gezinme deneyimini sağlamak için kullanılan teknik çerezler.</li>
        </ul>
      </section>

      {/* Section 3 */}
      <section id="isleme-amaclari" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
        <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
          <span className="text-brand-purple-500 font-mono text-lg mr-3">03.</span>
          Kişisel Verilerin İşlenme Amaçları ve Hukuki Sebepleri
        </h2>
        <p className="text-gray-600 leading-relaxed font-light">
          Toplanan kişisel verileriniz, KVKK m. 5 ve m. 6 ile GDPR m. 6’da belirtilen hukuki sebeplere dayanarak aşağıdaki amaçlarla işlenmektedir:
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
          <div className="p-5 bg-white rounded-2xl border border-brand-purple-100 shadow-sm space-y-2">
            <h4 className="font-bold text-brand-purple-900 text-sm">İletişim ve Destek Talepleri</h4>
            <p className="text-xs text-gray-500 font-light leading-relaxed">
              İletişim formları üzerinden gönderilen soru, görüş, öneri ve destek taleplerinizin değerlendirilmesi ve yanıtlanması.
            </p>
          </div>
          <div className="p-5 bg-white rounded-2xl border border-brand-purple-100 shadow-sm space-y-2">
            <h4 className="font-bold text-brand-purple-900 text-sm">Bilgi Güvenliği ve Sistem Yönetimi</h4>
            <p className="text-xs text-gray-500 font-light leading-relaxed">
              Web sitemizin güvenliğinin sağlanması, siber saldırıların engellenmesi ve 5651 sayılı kanun kapsamındaki log yükümlülükleri.
            </p>
          </div>
          <div className="p-5 bg-white rounded-2xl border border-brand-purple-100 shadow-sm space-y-2">
            <h4 className="font-bold text-brand-purple-900 text-sm">Yayın ve Rapor Sunumu</h4>
            <p className="text-xs text-gray-500 font-light leading-relaxed">
              Yayınlanan raporlar, haberler ve basın bültenlerine sorunsuz erişim imkanı tanınması.
            </p>
          </div>
          <div className="p-5 bg-white rounded-2xl border border-brand-purple-100 shadow-sm space-y-2">
            <h4 className="font-bold text-brand-purple-900 text-sm">Yasal Yükümlülüklerin İfası</h4>
            <p className="text-xs text-gray-500 font-light leading-relaxed">
              Yetkili kamu kurum ve kuruluşları tarafından talep edilmesi halinde yasal bilgi verme yükümlülüğünün yerine getirilmesi.
            </p>
          </div>
        </div>
      </section>

      {/* Section 4 */}
      <section id="verilerin-aktarimi" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
        <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
          <span className="text-brand-purple-500 font-mono text-lg mr-3">04.</span>
          Kişisel Verilerin Üçüncü Şahıslara Aktarımı
        </h2>
        <p className="text-gray-600 leading-relaxed font-light">
          TJA, toplanan kişisel verilerinizi <strong>ticari amaçlarla üçüncü şahıslara satmaz, kiralamaz ve reklam şirketleriyle kesinlikle paylaşmaz</strong>.
        </p>
        <p className="text-gray-600 leading-relaxed font-light">
          Verileriniz ancak açık rızanızın bulunması veya kanuni bir zorunluluk (mahkeme kararı, yetkili adli/idari merci talebi) oluşması hallerinde yalnızca ilgili resmi kurumlar ile sınırlı olarak paylaşılabilir.
        </p>
      </section>

      {/* Section 5 */}
      <section id="kvkk-haklari" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
        <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
          <span className="text-brand-purple-500 font-mono text-lg mr-3">05.</span>
          KVKK Madde 11 Kapsamındaki Haklarınız
        </h2>
        <p className="text-gray-600 leading-relaxed font-light">
          Veri sahibi olarak KVKK Madde 11 ve GDPR Madde 15-22 uyarınca aşağıdaki haklara sahipsiniz:
        </p>
        <div className="space-y-3">
          {[
            'Kişisel verilerinizin işlenip işlenmediğini öğrenme,',
            'İşlenmişse buna ilişkin bilgi talep etme,',
            'İşlenme amacını ve amacına uygun kullanılıp kullanılmadığını öğrenme,',
            'Yurt içinde veya yurt dışında aktarıldığı 3. kişileri bilme,',
            'Eksik veya yanlış işlenmişse düzeltilmesini isteme,',
            'KVKK m. 7 çerçevesinde verilerin silinmesini veya yok edilmesini isteme,',
            'İşlenen verilerin münhasıran otomatik sistemler vasıtasıyla analiz edilmesi suretiyle aleyhinize bir sonucun ortaya çıkmasına itiraz etme,',
            'Kanuna aykırı işleme sebebiyle zarara uğramanız halinde zararın giderilmesini talep etme.',
          ].map((hak, i) => (
            <div key={i} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
              <span className="w-5 h-5 rounded-full bg-brand-purple-100 text-brand-purple-700 flex items-center justify-center font-bold text-xs shrink-0 mt-0.5">
                ✓
              </span>
              <span className="text-sm text-gray-700 font-light">{hak}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Section 6 */}
      <section id="veri-guvenligi" className="scroll-mt-32 space-y-4 border-b border-gray-100 pb-10">
        <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
          <span className="text-brand-purple-500 font-mono text-lg mr-3">06.</span>
          Veri Güvenliği ve Saklama Süreleri
        </h2>
        <p className="text-gray-600 leading-relaxed font-light">
          Verilerinizin yetkisiz kişilerin eline geçmesini, kaybolmasını veya zarar görmesini önlemek amacıyla SSL/TLS şifreleme protokolleri ve güncel teknik güvenlik tedbirleri uygulanmaktadır.
        </p>
        <p className="text-gray-600 leading-relaxed font-light">
          İletişim verileriniz, talebiniz yanıtlandıktan ve yasal saklama süreleri dolduktan sonra ilk imha periyodunda güvenli bir şekilde silinir veya anonim hale getirilir.
        </p>
      </section>

      {/* Section 7 */}
      <section id="iletisim-basvuru" className="scroll-mt-32 space-y-4">
        <h2 className="text-2xl font-serif font-bold text-brand-purple-900 flex items-center">
          <span className="text-brand-purple-500 font-mono text-lg mr-3">07.</span>
          Başvuru ve İletişim Kanalları
        </h2>
        <p className="text-gray-600 leading-relaxed font-light">
          KVKK kapsamındaki haklarınızı kullanmak için kimliğinizi doğrulayan belgeler ile birlikte başvurunuzu aşağıdaki kanaldan tarafımıza iletebilirsiniz:
        </p>
        <div className="bg-brand-purple-900 text-white p-8 rounded-3xl space-y-4 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-brand-yellow-400/10 rounded-full blur-xl"></div>
          <h4 className="font-serif font-bold text-xl text-brand-yellow-400">Veri Sorumlusu İrtibat Bürosu</h4>
          <p className="text-sm text-brand-purple-100 font-light leading-relaxed">
            Başvurularınız KVKK m. 13 uyarınca en geç <strong>30 (otuz) gün</strong> içerisinde ücretsiz olarak sonuçlandırılacaktır.
          </p>
          <div className="pt-2 flex items-center space-x-3">
            <span className="text-xs uppercase tracking-widest text-brand-purple-200">E-Posta:</span>
            <a href="mailto:tja.bilgi@gmail.com" className="font-bold text-white hover:text-brand-yellow-400 underline">
              tja.bilgi@gmail.com
            </a>
          </div>
        </div>
      </section>
    </LegalLayout>
  );
};

export default PrivacyPolicy;
